const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

let cancelActive: (() => void) | null = null;

/**
 * Scroll the window to an element with a controlled, eased animation instead of
 * the browser's native `smooth` (which is very fast and uneven on long jumps).
 * Duration grows with distance (clamped), the user can interrupt with wheel /
 * touch / key input, and reduced-motion users get an instant jump.
 * Returns a cancel function.
 */
export function smoothScrollToElement(
  target: HTMLElement | null,
  { offset = 24, minDuration = 700, maxDuration = 1400 }: { offset?: number; minDuration?: number; maxDuration?: number } = {}
): () => void {
  if (!target) return () => undefined;
  cancelActive?.();

  const startY = window.scrollY;
  const endY = Math.max(0, target.getBoundingClientRect().top + startY - offset);
  const distance = endY - startY;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(distance) < 2) {
    window.scrollTo(0, endY);
    return () => undefined;
  }

  const duration = Math.min(maxDuration, Math.max(minDuration, 400 + Math.abs(distance) * 0.35));
  let frame = 0;
  let startTime: number | null = null;

  const stop = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
    if (cancelActive === stop) cancelActive = null;
  };

  const step = (now: number) => {
    if (startTime === null) startTime = now;
    const t = Math.min(1, (now - startTime) / duration);
    window.scrollTo(0, startY + distance * easeInOutCubic(t));
    if (t < 1) frame = requestAnimationFrame(step);
    else stop();
  };

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  cancelActive = stop;
  frame = requestAnimationFrame(step);
  return stop;
}
